"use client";

import type { ReactNode } from "react";
import { toast as sonnerToast } from "sonner";

import type { Action, ExternalToast } from "sonner";

/**
 * `toast()` / `useToast()` — a thin adapter over the single mounted sonner
 * `<Toaster>` (`components/ui/toaster.tsx`, mounted in `app/layout.tsx`).
 *
 * This module used to own a standalone shadcn reducer store whose only
 * renderer was a Radix `<ToastViewport>`. That viewport was never mounted
 * anywhere in the app, so every call through this hook produced no visible
 * output at all — including the global TanStack mutation-error handler in
 * `providers/query-client.tsx`, which meant failed mutations failed silently
 * for every user. The orphaned primitive (`components/ui/toast.tsx`) has since
 * been deleted so nobody can mount a second viewport and split the system
 * again.
 *
 * The call signature is deliberately unchanged (`toast({ title, description,
 * variant, action })`) so the existing call sites keep working; the payload is
 * now forwarded to sonner. Do NOT mount a second viewport.
 */

/** Variants the retired shadcn `Toast` accepted, plus sonner's own severities. */
export type ToastVariant =
  | "default"
  | "destructive"
  | "success"
  | "warning"
  | "info";

export interface ToastInput {
  title?: ReactNode;
  description?: ReactNode;
  /** `"destructive"` maps to `sonner.toast.error`; everything else to a plain toast. */
  variant?: ToastVariant | null;
  /** A rendered node (e.g. a `<Button>`) or sonner's `{ label, onClick }` shape. */
  action?: Action | ReactNode;
  duration?: number;
  id?: string | number;
  className?: string;
}

export interface ToastHandle {
  id: string | number;
  dismiss: () => void;
  update: (props: ToastInput) => void;
}

/**
 * Sonner takes a single `message` plus a `description`. Callers here pass
 * `{ title, description }`, `{ title }` alone, or (rarely) `{ description }`
 * alone — collapse that to the pair sonner expects without losing either half.
 */
function splitMessage(props: ToastInput): {
  message: ReactNode;
  description: ReactNode | undefined;
} {
  if (props.title !== undefined && props.title !== null) {
    return { message: props.title, description: props.description };
  }
  return { message: props.description ?? "", description: undefined };
}

function toExternal(props: ToastInput, id?: string | number): ExternalToast {
  const { description } = splitMessage(props);
  return {
    id: id ?? props.id,
    description,
    action: props.action,
    duration: props.duration,
    className: props.className,
  };
}

function emit(props: ToastInput, id?: string | number): string | number {
  const { message } = splitMessage(props);
  const options = toExternal(props, id);

  switch (props.variant) {
    case "destructive":
      return sonnerToast.error(message, options);
    case "success":
      return sonnerToast.success(message, options);
    case "warning":
      return sonnerToast.warning(message, options);
    case "info":
      return sonnerToast.info(message, options);
    // Spelled out rather than left to `default:` so the exhaustiveness check
    // fails loudly when a new variant is added instead of silently routing it
    // to the plain toast.
    case "default":
    case null:
    case undefined:
      return sonnerToast(message, options);
  }
}

export function toast(props: ToastInput): ToastHandle {
  const id = emit(props);

  return {
    id,
    dismiss: () => {
      sonnerToast.dismiss(id);
    },
    // Sonner updates in place when re-emitted under the same id.
    update: (next: ToastInput) => {
      emit({ ...props, ...next }, id);
    },
  };
}

// Keeps the `use` prefix despite calling no hooks: this is the shadcn/ui
// `useToast()` API that 38 call sites import, and it stays a hook-shaped
// accessor so it can grow state later without a breaking rename.
// eslint-disable-next-line @eslint-react/no-unnecessary-use-prefix
export function useToast(): {
  toast: (props: ToastInput) => ToastHandle;
  dismiss: (toastId?: string | number) => void;
} {
  return {
    toast,
    dismiss: (toastId?: string | number) => {
      sonnerToast.dismiss(toastId);
    },
  };
}
